import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConversationMessage } from '../realtime-session-stream/conversation-message.interface';
import { ChatMessage, ChatStorage, ChatSession, GroupedSessions } from '@app/interfaces';

/**
 * UNIFIED conversation persistence service.
 * Handles ALL storage operations and cognitive state management.
 * Replaces the old ConversationHistoryStorageService.
 */
@Injectable({
    providedIn: 'root'
})
export class ConversationPersistenceService {
    // Session management
    private readonly sessionsListChanged$: Subject<boolean> = new Subject<boolean>();
    private readonly conversationStorage: ChatStorage = {};
    private sessionId: number = -1;

    // Storage keys
    private readonly STORAGE_CONVERSATION_HISTORY_SUFFIX = 'streaming_conversation_history';
    private readonly STORAGE_CONVERSATION_MESSAGES_SUFFIX = 'streaming_conversation_messages';
    private readonly COGNITIVE_STATE_STORAGE_PREFIX = 'cognitive_state_';

    /**
     * Save messages to storage
     */
    saveConversation(messages: ConversationMessage[]): void {
        if (this.sessionId === -1) {
            console.warn('⚠️ No active session to save');
            return;
        }

        // CRITICAL FIX: Before clearing conversation, preserve cognitive states from existing stored messages
        // This prevents follow-up responses from corrupting cognitive states of previous messages
        const existingMessages = this.getConversationMessagesById(this.sessionId);
        const cognitiveStateBackup = new Map<string, any>();

        if (existingMessages?.length > 0) {
            existingMessages.forEach((msg: any) => {
                if (msg.messageId && msg.cognitiveState) {
                    cognitiveStateBackup.set(msg.messageId, msg.cognitiveState);
                }
            });
            // console.log('🔒 Backed up cognitive states:', cognitiveStateBackup.size, 'messages');
        }

        // console.log('💾 Persisting', messages.length, 'messages to storage');

        // Clear and re-add all messages atomically
        this.clearCurrentConversation();

        // Convert to ChatMessage format for storage
        const chatMessages: ChatMessage[] = messages.map((msg) => {
            // CRITICAL FIX: Restore cognitive state from backup if current message doesn't have it
            let cognitiveState = msg.cognitiveState;
            if (!cognitiveState && msg.messageId && cognitiveStateBackup.has(msg.messageId)) {
                cognitiveState = cognitiveStateBackup.get(msg.messageId);
            }

            return {
                content: msg.content,
                role: msg.role,
                requestId: msg.requestId,
                messageType: msg.messageType,
                ...(msg.messageId && { messageId: msg.messageId }),
                ...(msg.timestamp && { timestamp: msg.timestamp }),
                ...(cognitiveState && { cognitiveState: cognitiveState }),
                ...(msg.streamingState && { streamingState: msg.streamingState }),
                ...(msg.feedbackData && { feedbackData: msg.feedbackData }),
                ...(msg.feedbackState && { feedbackState: msg.feedbackState }),
                ...(msg.datasheetState && { datasheetState: msg.datasheetState })
                
            } as any;
        });

        // Add all messages back
        chatMessages.forEach((message) => {
            this.addMessageToConversationHistory(message, this.sessionId);
        });

        // Also save cognitive state separately
        this.saveCognitiveState(messages, this.sessionId);

        // console.log('✅ Saved complete conversation');
    }

    /**
     * Load conversation from storage
     */
    loadConversation(sessionId: number): ConversationMessage[] {
        const messages = this.getConversationMessagesById(sessionId);

        if (!messages || messages.length === 0) {
            console.warn('⚠️ No messages found for session:', sessionId);
            return [];
        }

        // Restore cognitive states
        const messagesWithCognitive = this.restoreCognitiveStates(messages, sessionId);

        // Convert to ConversationMessage format
        return messagesWithCognitive.map((msg) => this.convertToConversationMessage(msg));
    }

    /**
     * Save cognitive state to localStorage
     */
    private saveCognitiveState(messages: ConversationMessage[], sessionId: number): void {
        try {
            // Store with session-specific key
            const key = `craig_cognitive_state_${sessionId}`;

            // CRITICAL FIX: Ensure we preserve all message identifiers for proper restoration
            const messagesToStore = messages.map((msg) => ({
                ...msg,
                cognitiveState: msg.cognitiveState,
                // Ensure these identifiers are always present for lookup
                messageId: msg.messageId,
                requestId: msg.requestId,
                timestamp: msg.timestamp,
                messageType: msg.messageType
            }));

            localStorage.setItem(key, JSON.stringify(messagesToStore));

            // Track keys for cleanup
            localStorage.setItem(`craig_last_cognitive_key_${sessionId}`, key);
            localStorage.setItem('craig_last_cognitive_key', key);

            // DIAGNOSTIC: Log cognitive state save details
            const cognitiveMessageCount = messages.filter((m) => m.cognitiveState).length;
            const cognitiveStateDetails = messages
                .filter((m) => m.cognitiveState)
                .map((m) => ({
                    messageId: m.messageId,
                    requestId: m.requestId,
                    hasCognitiveTree:
                        !!(m.cognitiveState as any)?.cognitiveTree?.length ||
                        !!(m.cognitiveState as any)?.rootSpans?.length,
                    treeNodeCount:
                        (m.cognitiveState as any)?.cognitiveTree?.length ||
                        (m.cognitiveState as any)?.rootSpans?.length ||
                        0
                }));

            // console.log('💾 Saved cognitive state:', {
            //     key,
            //     sessionId,
            //     totalMessages: messages.length,
            //     messagesWithCognitiveState: cognitiveMessageCount,
            //     cognitiveStateDetails
            // });
        } catch (error: any) {
            console.error('Failed to save cognitive state:', error);

            // Handle quota exceeded
            if (error?.name === 'QuotaExceededError') {
                this.cleanupOldCognitiveStates();
                // Retry with original data
                try {
                    const key = `craig_cognitive_state_${sessionId}`;
                    const messagesToStore = messages.map((msg) => ({
                        ...msg,
                        cognitiveState: msg.cognitiveState,
                        messageId: msg.messageId,
                        requestId: msg.requestId,
                        timestamp: msg.timestamp,
                        messageType: msg.messageType
                    }));
                    localStorage.setItem(key, JSON.stringify(messagesToStore));
                    console.log('✅ Cognitive state saved after cleanup');
                } catch (retryError) {
                    console.error('❌ Failed to save after cleanup:', retryError);
                }
            }
        }
    }

    /**
     * Restore cognitive states from localStorage
     */
    private restoreCognitiveStates(messages: ChatMessage[], sessionId: number): ChatMessage[] {
        try {
            // Try session-specific keys
            const sessionKeys = [
                `craig_cognitive_state_${sessionId}`,
                localStorage.getItem(`craig_last_cognitive_key_${sessionId}`)
            ].filter((k) => k);

            let storedMessages: ConversationMessage[] | null = null;

            for (const key of sessionKeys) {
                if (key && localStorage.getItem(key)) {
                    const stored = localStorage.getItem(key);
                    if (stored) {
                        storedMessages = JSON.parse(stored);
                        // console.log('✅ Restored cognitive state from key:', key);
                        break;
                    }
                }
            }

            // Fallback to global key
            if (!storedMessages) {
                const globalKey = localStorage.getItem('craig_last_cognitive_key');
                if (globalKey) {
                    const stored = localStorage.getItem(globalKey);
                    if (stored) {
                        storedMessages = JSON.parse(stored);
                        // console.log('⚠️ Using fallback global key:', globalKey);
                    }
                }
            }

            // CRITICAL FIX: Merge cognitive states by messageId/requestId instead of array index
            if (storedMessages) {
                // Create lookup map by messageId and requestId for faster matching
                const storedLookup = new Map<string, ConversationMessage>();

                storedMessages.forEach((storedMsg) => {
                    // Primary key: messageId
                    if (storedMsg.messageId) {
                        storedLookup.set(storedMsg.messageId, storedMsg);
                    }
                    // Secondary key: requestId (for backward compatibility)
                    if (storedMsg.requestId) {
                        storedLookup.set(`req_${storedMsg.requestId}`, storedMsg);
                    }
                });

                let matchedCount = 0;
                let cognitiveRestoreCount = 0;

                const restoredMessages = messages.map((msg) => {
                    const msgAny = msg as any;

                    // Try to find matching stored message by messageId first
                    let storedMsg = msgAny.messageId ? storedLookup.get(msgAny.messageId) : null;

                    // Fallback to requestId if messageId lookup failed
                    if (!storedMsg && msgAny.requestId) {
                        storedMsg = storedLookup.get(`req_${msgAny.requestId}`);
                    }

                    if (storedMsg) {
                        matchedCount++;

                        // If we found a match with cognitive state, merge it
                        if (storedMsg?.cognitiveState) {
                            cognitiveRestoreCount++;
                            return {
                                ...msg,
                                cognitiveState: storedMsg.cognitiveState
                            } as any;
                        }
                    }

                    return msg;
                });

                // DIAGNOSTIC: Log restoration results
                // console.log('✅ Cognitive state restoration completed:', {
                //     sessionId,
                //     totalMessages: messages.length,
                //     storedMessages: storedMessages.length,
                //     messagesMatched: matchedCount,
                //     cognitiveStatesRestored: cognitiveRestoreCount,
                //     unmatched: messages.length - matchedCount
                // });

                return restoredMessages;
            }
        } catch (error) {
            console.error('Failed to restore cognitive state:', error);
        }

        return messages;
    }

    /**
     * Convert ChatMessage to ConversationMessage
     */
    private convertToConversationMessage(msg: ChatMessage): ConversationMessage {
        const existingMessageType = (msg as any).messageType;

        // Preserve feedback_prompt messages
        if (existingMessageType === 'feedback_prompt') {
            return {
                ...msg,
                messageId: (msg as any).messageId || this.generateMessageId(),
                timestamp: (msg as any).timestamp || Date.now(),
                datasheetState: (msg as any).datasheetState
            } as ConversationMessage;
        }

        // Convert regular messages
        return {
            ...msg,
            messageType: existingMessageType || (msg.role === 'user' ? 'user' : 'assistant'),
            messageId: (msg as any).messageId || this.generateMessageId(),
            timestamp: (msg as any).timestamp || Date.now(),
            cognitiveState: (msg as any).cognitiveState,
            streamingState: (msg as any).streamingState,
            datasheetState: (msg as any).datasheetState
        } as ConversationMessage;
    }

    /**
     * Clean up old cognitive states to free storage
     */
    private cleanupOldCognitiveStates(): void {
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('craig_cognitive_state_')) {
                keysToRemove.push(key);
            }
        }

        // Remove oldest half
        const toRemove = Math.floor(keysToRemove.length / 2);
        keysToRemove.slice(0, toRemove).forEach((key) => {
            localStorage.removeItem(key);
            // console.log('🗑️ Removed old cognitive state:', key);
        });
    }

    private generateMessageId(): string {
        return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // ========================================================================
    // SESSION MANAGEMENT (consolidated from ConversationHistoryStorageService)
    // ========================================================================

    addNewConversation(sessionId?: number): number {
        const userId: string = this.getCurrentUserId();

        this.sessionId = sessionId || Date.now();
        this.conversationStorage[this.sessionId] = [...this.getStoredConversationMessages(userId, this.sessionId)];

        return this.sessionId;
    }

    addMessageToConversationHistory(message: ChatMessage, sessionId: number = this.sessionId): void {
        if (!this.conversationStorage[sessionId]) {
            this.conversationStorage[sessionId] = [];
        }

        this.conversationStorage[sessionId].push(message);
        this.saveMessagesToLocalStorage(this.conversationStorage[sessionId], sessionId);
    }

    getCurrentConversationId(): number {
        return this.sessionId;
    }

    getCurrentConversationMessages(): ChatMessage[] {
        return this.conversationStorage[this.sessionId] || [];
    }

    clearCurrentConversation(): void {
        if (this.sessionId !== -1) {
            this.conversationStorage[this.sessionId] = [];
            const userId = this.getCurrentUserId();
            this.setStoredConversationMessages(userId, this.sessionId, []);
        }
    }

    startNewSession(): number {
        this.sessionId = Date.now();
        this.conversationStorage[this.sessionId] = [];
        return this.sessionId;
    }

    getConversationMessagesById(sessionId: number): ChatMessage[] {
        if (!this.conversationStorage[sessionId]) {
            // Try to load from localStorage if not in memory
            const userId = this.getCurrentUserId();
            this.conversationStorage[sessionId] = this.getStoredConversationMessages(userId, sessionId);
        }
        return this.conversationStorage[sessionId] || [];
    }

    getGroupedConversationSessions(): GroupedSessions[] {
        const userId: string = this.getCurrentUserId();
        return this.groupSessionsByMonth(this.getStoredSessions(userId));
    }

    changeSessionsList(): void {
        this.sessionsListChanged$.next(true);
    }

    conversationSessionsListChange(): Observable<boolean> {
        return this.sessionsListChanged$.asObservable();
    }

    // ========================================================================
    // PRIVATE STORAGE METHODS (consolidated from ConversationHistoryStorageService)
    // ========================================================================

    private getCurrentUserId(): string {
        return 'streaming_user';
    }

    private getStoredSessions(userId: string): ChatSession[] {
        try {
            const stored = localStorage.getItem(`${userId}_${this.STORAGE_CONVERSATION_HISTORY_SUFFIX}`);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('🔴 Failed to load sessions from localStorage:', error);
            return [];
        }
    }

    private setStoredSessions(userId: string, sessions: ChatSession[]): void {
        try {
            localStorage.setItem(`${userId}_${this.STORAGE_CONVERSATION_HISTORY_SUFFIX}`, JSON.stringify(sessions));
        } catch (error) {
            console.error('🔴 Failed to save sessions to localStorage:', error);
        }
    }

    private getStoredConversationMessages(userId: string, sessionId: number): ChatMessage[] {
        try {
            const stored = localStorage.getItem(`${userId}_${sessionId}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('🔴 Failed to load messages from localStorage:', error);
            return [];
        }
    }

    private setStoredConversationMessages(userId: string, sessionId: number, messages: ChatMessage[]): void {
        try {
            localStorage.setItem(
                `${userId}_${sessionId}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`,
                JSON.stringify(messages)
            );
        } catch (error) {
            console.error('🔴 Failed to save messages to localStorage:', error);
        }
    }

    private saveMessagesToLocalStorage(messages: ChatMessage[], sessionId: number, tryCount: number = 0): void {
        const userId: string = this.getCurrentUserId();

        try {
            this.saveSessionToLocalStorage(messages.length ? messages[0].content : '', userId, sessionId);
            this.setStoredConversationMessages(userId, sessionId, messages);
        } catch (error: any) {
            console.error('🔴 Failed to save to localStorage:', error);

            if (this.isStorageExceededError(error)) {
                this.cleanupOldSessions();

                if (tryCount === 0) {
                    this.saveMessagesToLocalStorage(messages, sessionId, tryCount + 1);
                }
            }
        }
    }

    private saveSessionToLocalStorage(title: string, userId: string, sessionId: number): void {
        const sessions: ChatSession[] = this.getStoredSessions(userId);

        if (!sessions.length || sessions.findIndex(({ id }: ChatSession) => id === sessionId) === -1) {
            const createDate: string = new Date(sessionId).toISOString();

            sessions.push({ id: sessionId, title, createDate });

            this.setStoredSessions(userId, sessions);
            this.changeSessionsList();
        }
    }

    private groupSessionsByMonth(sessions: ChatSession[]): GroupedSessions[] {
        const reversedSessions: ChatSession[] = [...sessions].reverse();
        const map: Map<string, ChatSession[]> = new Map();

        for (const session of reversedSessions) {
            const key: string = new Date(session.createDate).toLocaleString('en-US', {
                month: 'long',
                year: 'numeric'
            });

            if (!map.has(key)) {
                map.set(key, []);
            }

            map.get(key)!.push(session);
        }

        return Array.from(map.entries()).map(([monthYear, sessions]: [string, ChatSession[]]) => ({
            monthYear,
            sessions
        }));
    }

    private isStorageExceededError(error: any): boolean {
        return error?.name === 'QuotaExceededError' || error?.code === 22;
    }

    private cleanupOldSessions(): void {
        const userId: string = this.getCurrentUserId();
        const sessions: ChatSession[] = this.getStoredSessions(userId);

        if (sessions.length > 50) {
            // Keep only the 30 most recent sessions
            const sortedSessions = sessions.sort(
                (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
            );
            const sessionsToKeep = sortedSessions.slice(0, 30);
            const sessionsToDelete = sortedSessions.slice(30);

            // Delete old session messages
            sessionsToDelete.forEach((session) => {
                localStorage.removeItem(`${userId}_${session.id}_${this.STORAGE_CONVERSATION_MESSAGES_SUFFIX}`);
            });

            // Update sessions list
            this.setStoredSessions(userId, sessionsToKeep);
            this.changeSessionsList();
        }
    }
}
